import { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Calendar } from 'lucide-react';
import type { TimeRange } from '../../tabs/FleetIntelligence';

interface TimeSliderProps {
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  currentTimestamp: string | null;
  isPlaying: boolean;
  playbackSpeed: number;
  onPlaybackControlsChange: (playing: boolean, speed: number, timestamp: string | null) => void;
}

const SPEED_OPTIONS = [
  { value: 1, label: '1x' },
  { value: 2, label: '2x' },
  { value: 5, label: '5x' },
  { value: 10, label: '10x' },
];

export default function TimeSlider({
  timeRange,
  onTimeRangeChange,
  currentTimestamp,
  isPlaying,
  playbackSpeed,
  onPlaybackControlsChange,
}: TimeSliderProps) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempFromDate, setTempFromDate] = useState('');
  const [tempToDate, setTempToDate] = useState('');
  const [sliderValue, setSliderValue] = useState(0);
  const playbackInterval = useRef<NodeJS.Timeout | null>(null);

  // Initialize temp dates from timeRange
  useEffect(() => {
    setTempFromDate(timeRange.from.split('T')[0]);
    setTempToDate(timeRange.to.split('T')[0]);
  }, [timeRange]);

  // Playback logic
  useEffect(() => {
    if (!isPlaying) {
      if (playbackInterval.current) {
        clearInterval(playbackInterval.current);
        playbackInterval.current = null;
      }
      return;
    }

    const fromTime = new Date(timeRange.from).getTime();
    const toTime = new Date(timeRange.to).getTime();
    const duration = toTime - fromTime;

    // Start from current timestamp or beginning
    let currentTime = currentTimestamp
      ? new Date(currentTimestamp).getTime()
      : fromTime;

    // If we're at the end, restart from beginning
    if (currentTime >= toTime) {
      currentTime = fromTime;
    }

    // Update interval based on playback speed (update every 100ms, but advance time faster)
    const intervalMs = 100;
    const timeAdvance = (duration / 100) * (playbackSpeed / 10); // Advance proportional to speed

    playbackInterval.current = setInterval(() => {
      currentTime += timeAdvance;

      if (currentTime >= toTime) {
        // Reached end, stop playback
        onPlaybackControlsChange(false, playbackSpeed, new Date(toTime).toISOString());
        if (playbackInterval.current) {
          clearInterval(playbackInterval.current);
          playbackInterval.current = null;
        }
      } else {
        const newTimestamp = new Date(currentTime).toISOString();
        const progress = ((currentTime - fromTime) / duration) * 100;
        setSliderValue(progress);
        onPlaybackControlsChange(true, playbackSpeed, newTimestamp);
      }
    }, intervalMs);

    return () => {
      if (playbackInterval.current) {
        clearInterval(playbackInterval.current);
        playbackInterval.current = null;
      }
    };
  }, [isPlaying, playbackSpeed, timeRange, currentTimestamp, onPlaybackControlsChange]);

  // Update slider value when currentTimestamp changes externally
  useEffect(() => {
    if (currentTimestamp) {
      const fromTime = new Date(timeRange.from).getTime();
      const toTime = new Date(timeRange.to).getTime();
      const currentTime = new Date(currentTimestamp).getTime();
      const progress = ((currentTime - fromTime) / (toTime - fromTime)) * 100;
      setSliderValue(Math.max(0, Math.min(100, progress)));
    } else {
      setSliderValue(0);
    }
  }, [currentTimestamp, timeRange]);

  const handlePlayPause = () => {
    if (isPlaying) {
      onPlaybackControlsChange(false, playbackSpeed, currentTimestamp);
    } else {
      onPlaybackControlsChange(true, playbackSpeed, currentTimestamp);
    }
  };

  const handleReset = () => {
    onPlaybackControlsChange(false, playbackSpeed, null);
    setSliderValue(0);
  };

  const handleSpeedChange = (newSpeed: number) => {
    onPlaybackControlsChange(isPlaying, newSpeed, currentTimestamp);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setSliderValue(value);

    // Calculate timestamp from slider value
    const fromTime = new Date(timeRange.from).getTime();
    const toTime = new Date(timeRange.to).getTime();
    const duration = toTime - fromTime;
    const newTime = fromTime + (duration * value) / 100;
    const newTimestamp = new Date(newTime).toISOString();

    onPlaybackControlsChange(false, playbackSpeed, newTimestamp);
  };

  const handleYesterday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const from = new Date(yesterday);
    from.setHours(0, 0, 0, 0);
    
    const to = new Date(yesterday);
    to.setHours(23, 59, 59, 999);
    
    onTimeRangeChange({
      from: from.toISOString(),
      to: to.toISOString(),
    });
    
    setShowDatePicker(false);
  };

  const handleQuickTimeRange = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    
    onTimeRangeChange({
      from: from.toISOString(),
      to: to.toISOString(),
    });
    
    setShowDatePicker(false);
  };

  const handleApplyCustomRange = () => {
    if (!tempFromDate || !tempToDate) return;
    
    const from = new Date(tempFromDate + 'T00:00:00Z');
    const to = new Date(tempToDate + 'T23:59:59Z');
    
    if (from > to) {
      alert('Start date must be before end date');
      return;
    }
    
    onTimeRangeChange({
      from: from.toISOString(),
      to: to.toISOString(),
    });
    
    setShowDatePicker(false);
  };

  const formatTimestamp = (timestamp: string | null): string => {
    if (!timestamp) return 'Not started';
    return new Date(timestamp).toLocaleString();
  };

  const formatTimeRange = (): string => {
    const from = new Date(timeRange.from);
    const to = new Date(timeRange.to);
    return `${from.toLocaleDateString()} - ${to.toLocaleDateString()}`;
  };

  return (
    <div className="bg-card rounded-lg p-4 border border-border space-y-4">
      {/* Time Range Display and Date Picker */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-white mb-1">Time Range</h3>
          <p className="text-xs text-muted-foreground">{formatTimeRange()}</p>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground bg-secondary rounded-md hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent"
          >
            <Calendar className="w-4 h-4" />
            Change Range
          </button>
          
          {showDatePicker && (
            <div className="absolute top-full right-0 mt-2 bg-popover border border-border rounded-lg p-4 shadow-xl z-10 min-w-75">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-foreground mb-1">Quick Select</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleYesterday}
                      className="px-3 py-1.5 text-xs bg-ring hover:bg-ring text-white rounded transition-colors font-medium"
                    >
                      Yesterday
                    </button>
                    <button
                      onClick={() => handleQuickTimeRange(1)}
                      className="px-3 py-1.5 text-xs bg-accent hover:bg-accent text-white rounded transition-colors"
                    >
                      Last 24h
                    </button>
                    <button
                      onClick={() => handleQuickTimeRange(7)}
                      className="px-3 py-1.5 text-xs bg-accent hover:bg-accent text-white rounded transition-colors"
                    >
                      Last 7 days
                    </button>
                    <button
                      onClick={() => handleQuickTimeRange(30)}
                      className="px-3 py-1.5 text-xs bg-accent hover:bg-accent text-white rounded transition-colors"
                    >
                      Last 30 days
                    </button>
                  </div>
                </div>
                
                <div className="border-t border-border pt-3">
                  <label className="block text-xs text-foreground mb-2">Custom Range</label>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">From</label>
                      <input
                        type="date"
                        value={tempFromDate}
                        onChange={(e) => setTempFromDate(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm bg-accent border border-border rounded text-white focus:outline-none focus:border-ring"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">To</label>
                      <input
                        type="date"
                        value={tempToDate}
                        onChange={(e) => setTempToDate(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm bg-accent border border-border rounded text-white focus:outline-none focus:border-ring"
                      />
                    </div>
                    <button
                      onClick={handleApplyCustomRange}
                      className="w-full px-3 py-1.5 text-sm bg-primary hover:bg-primary text-white rounded transition-colors"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Playback Controls */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          {/* Play/Pause */}
          <button
            onClick={handlePlayPause}
            className="p-2 bg-primary hover:bg-primary rounded-lg transition-colors group"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 text-white" />
            ) : (
              <Play className="w-5 h-5 text-white" />
            )}
          </button>

          {/* Reset */}
          <button
            onClick={handleReset}
            className="p-2 bg-secondary hover:bg-accent rounded-lg transition-colors"
            title="Reset to start"
          >
            <RotateCcw className="w-5 h-5 text-foreground" />
          </button>

          {/* Speed Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Speed:</span>
            {SPEED_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSpeedChange(option.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                  playbackSpeed === option.value
                    ? 'bg-primary text-white'
                    : 'bg-secondary text-foreground hover:bg-accent'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Current Timestamp Display */}
          <div className="ml-auto text-xs text-muted-foreground">
            {formatTimestamp(currentTimestamp)}
          </div>
        </div>

        {/* Timeline Slider */}
        <div className="space-y-1">
          <input
            type="range"
            min="0"
            max="100"
            step="0.1"
            value={sliderValue}
            onChange={handleSliderChange}
            className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer slider-thumb"
            style={{
              background: `linear-gradient(to right, rgb(var(--success)) 0%, rgb(var(--success)) ${sliderValue}%, rgb(var(--secondary)) ${sliderValue}%, rgb(var(--secondary)) 100%)`,
            }}
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{new Date(timeRange.from).toLocaleString()}</span>
            <span>{new Date(timeRange.to).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Custom slider thumb styling */}
      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: rgb(var(--success));
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        .slider-thumb::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: rgb(var(--success));
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  );
}
